'use client';

import { ReactNode } from 'react';
import { TwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout';
import { CompanyJobsSidebar } from '@components/features/company-jobs-sidebar/CompanyJobsSidebar';
import { CandidateJobsSidebar } from '@components/features/candidate-jobs-sidebar';
import { SidebarNavigationProvider } from '@components/contexts/SidebarNavigationContext';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';

interface LayoutWithTwoColumnsProps {
  children: ReactNode;
  locale?: Locale;
  isAuthenticated?: boolean;
}

/**
 * @description Layout wrapper for pages that need two-column layout.
 *
 * Users can switch between candidate and company contexts using the "Switch Context" button.
 * Context preference is saved in localStorage and persists across sessions.
 *
 * Left sidebar:
 *   - Candidate context: Job search alerts
 *   - Company context: Company navigation
 * Main content: Page content
 */
export const LayoutWithTwoColumns = (props: LayoutWithTwoColumnsProps) => {
  const { children, locale: propLocale, isAuthenticated: propIsAuthenticated } = props;
  const { locale: routerLocale } = useExtendedRouter();
  const locale: Locale = propLocale || routerLocale || 'ro';

  // Get authentication status and user info
  const { headerInfoData, isSessionAuthenticated } = useHeaderInfoData(locale);
  const isAuthenticated = propIsAuthenticated ?? isSessionAuthenticated ?? !!headerInfoData;

  // Get current context from localStorage (candidate or company)
  const { currentContext, switchContext } = useMissionControlContext();

  // On context switch, reset navigation to the /mc base so residual URLs (e.g. /mc/job-add/edit) are cleared.
  const { router } = useExtendedRouter();
  const handleSwitchContext = () => {
    switchContext();
    router.push('/mc');
  };

  // Show company context if selected
  if (currentContext === 'company') {
    const handleJobSelect = (job: ApiSchema<'MyJobCard'>) => {
      window.location.href = `/${locale}/mc/job/${job.slug}/applicants`;
    };
    const handleTagClick = (job: ApiSchema<'MyJobCard'>, status: number) => {
      window.location.href = `/${locale}/mc/job/${job.slug}/applicants?status=${status}`;
    };

    return (
      <TwoColumnLayout
        leftSidebar={(
          <CompanyJobsSidebar
            locale={locale}
            isAuthenticated={isAuthenticated}
            onSwitchContext={handleSwitchContext}
            onJobSelect={handleJobSelect}
            onTagClick={handleTagClick}
          />
        )}
        mainContent={children}
        rightSidebar={undefined}
      />
    );
  }

  // Show candidate context (default)
  return (
    <SidebarNavigationProvider initialView="new-search" locale={locale}>
      <TwoColumnLayout
        leftSidebar={(
          <CandidateJobsSidebar
            locale={locale}
            isAuthenticated={isAuthenticated}
            onSwitchContext={handleSwitchContext}
          />
        )}
        mainContent={children}
        rightSidebar={undefined}
      />
    </SidebarNavigationProvider>
  );
};
